import type { ValueGetterFunc, ValueGetterParams } from "ag-grid-community";

const getByPath = (obj: unknown, path: string): unknown =>
  path
    .split(".")
    .reduce<unknown>(
      (acc, key) =>
        acc !== null && acc !== undefined && typeof acc === "object"
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      obj,
    );

const resolveTemplate = (
  template: string,
  column: Record<string, unknown>,
  row: Record<string, unknown>,
): string =>
  template.replace(
    /\{(column|row)\.([^}]+)\}/g,
    (_, dimension: string, key: string) =>
      String(dimension === "column" ? column[key] : row[key]),
  );

/**
 * Builds a ValueGetterFunc that closes over a column descriptor and delegates
 * value computation to a caller-supplied function. The function receives the
 * typed grid context, the row, and the column — so callers can express any
 * row×column lookup without boilerplate.
 */
export const makeValueGetter =
  <TContext, TRow, TColumn, TValue>(
    column: TColumn,
    getValueFn: (context: TContext, row: TRow, column: TColumn) => TValue,
  ): ValueGetterFunc<TRow, TValue | null> =>
  (params: ValueGetterParams<TRow, TValue | null>): TValue | null => {
    if (!params.data) return null;
    return getValueFn(params.context as TContext, params.data, column);
  };

/**
 * Path-based alternative to makeValueGetter for simpler grids where cell
 * values are plain context lookups rather than computations.
 *
 * Template syntax: dot-separated path into the grid context, with
 * `{column.key}` and `{row.key}` tokens interpolated at call time.
 *
 * Example: "payload.periods.{column.period}.coverages.{row.coverageId}.annualPremium"
 */
export const makeValueGetterFromPath =
  <TColumn extends object>(
    column: TColumn,
    template: string,
  ): ValueGetterFunc =>
  (params: ValueGetterParams): unknown => {
    if (!params.data) return null;
    const path = resolveTemplate(
      template,
      column as Record<string, unknown>,
      params.data as Record<string, unknown>,
    );
    return getByPath(params.context, path);
  };
